/** Dominio de producción Pinturas: fórmulas, lotes/OP y etapas de inventario. */

import { inventoryQtyFromPurchase } from '../../utils/packFormatQty';
import {
  getActiveProductionCatalogOptions,
  getProductionCatalogColorMap,
} from './productionCatalogDefaults';

export const FORMULA_STATUSES = ['Borrador', 'Activa', 'Obsoleta'];

export const FORMULA_STATUS_COLORS = {
  Borrador: '#64748b',
  Activa: '#16a34a',
  Obsoleta: '#9ca3af',
};

export const FORMULA_KIND_BASE = 'base';
export const FORMULA_KIND_COLOR = 'color';

export const FORMULA_KIND_LABELS = {
  base: 'Bases',
  color: 'Colores',
};

export const formulaKindLabel = (kind) =>
  kind === FORMULA_KIND_COLOR ? 'Color' : 'Base';

export const normalizeFormulaKind = (row) => {
  if (row?.formula_kind === FORMULA_KIND_COLOR || row?.formula_kind === FORMULA_KIND_BASE) {
    return row.formula_kind;
  }
  const hay = `${row?.code || ''} ${row?.product_name || ''} ${row?.color_name || ''} ${row?.notes || ''}`.toLowerCase();
  if (/^fo-c-/.test(String(row?.code || '').toLowerCase())) return FORMULA_KIND_COLOR;
  if (/\b(color|gris|ral|esmalte|símil)\b/.test(hay)) return FORMULA_KIND_COLOR;
  return FORMULA_KIND_BASE;
};

export const BATCH_STATUSES = [
  'Borrador',
  'Planificada',
  'Emitida',
  'En proceso',
  'Fabricado',
  'En calidad',
  'Reproceso',
  'Terminado',
  'Anulada',
];

export const BATCH_STATUS_COLORS = {
  Borrador: '#9ca3af',
  Planificada: '#2563eb',
  Emitida: '#0ea5e9',
  'En proceso': '#d97706',
  Fabricado: '#7c3aed',
  'En calidad': '#c026d3',
  Reproceso: '#c2410c',
  Terminado: '#15803d',
  Anulada: '#dc2626',
};

export const BATCH_ACCOUNTING_EVENTS = {
  Emitida: 'production.issued',
  Fabricado: 'production.manufactured',
  Terminado: 'production.finished',
};

export const BATCH_TAB_STATUSES = {
  all: BATCH_STATUSES,
  plant: ['Emitida', 'En proceso', 'Reproceso'],
  quality: ['Fabricado', 'En calidad'],
  done: ['Terminado', 'Anulada'],
};

/** Estados de lote activos desde Producción → Ajustes. */
export const getActiveBatchStatuses = () => {
  const options = getActiveProductionCatalogOptions('batch_status');
  const labels = options.map((opt) => opt.value).filter(Boolean);
  return labels.length ? labels : BATCH_STATUSES;
};

export const getBatchStatusColorMap = () => ({
  ...BATCH_STATUS_COLORS,
  ...getProductionCatalogColorMap('batch_status'),
});

export const getBatchStatusColor = (status) =>
  getBatchStatusColorMap()[String(status || '').trim()] || '#64748b';

/** Estados de fórmula activos desde Producción → Ajustes. */
export const getActiveFormulaStatuses = () => {
  const options = getActiveProductionCatalogOptions('formula_status');
  const labels = options.map((opt) => opt.value).filter(Boolean);
  return labels.length ? labels : FORMULA_STATUSES;
};

export const getFormulaStatusColorMap = () => ({
  ...FORMULA_STATUS_COLORS,
  ...getProductionCatalogColorMap('formula_status'),
});

export const getFormulaStatusColor = (status) =>
  getFormulaStatusColorMap()[String(status || '').trim()] || '#64748b';

export const PAINT_PRODUCT_OPTIONS = [
  { value: 'A006', label: 'A006 · Pintura epóxica gris T-COAT' },
  { value: 'BW01', label: 'BW01 · Primer blanco T-COAT' },
  { value: 'A012', label: 'A012 · Esmalte acrílico interior' },
  { value: 'BS005', label: 'BS005 · Diluyente epóxico' },
];

export const MATERIAL_CATALOG = [
  { code: 'RS0001', name: 'Resina epóxica', kind: 'Materias primas', unit: 'kg', stock: 1840 },
  { code: 'PG0001', name: 'Dióxido de titanio', kind: 'Materias primas', unit: 'kg', stock: 620 },
  { code: 'PG0002', name: 'Negro de humo furnace', kind: 'Materias primas', unit: 'kg', stock: 24 },
  { code: 'DY0001', name: 'Xileno', kind: 'Materias primas', unit: 'L', stock: 410 },
  { code: 'EX0001', name: 'Carbonato de calcio', kind: 'Materias primas', unit: 'kg', stock: 960 },
  { code: 'AD0001', name: 'Antiespumante siliconado', kind: 'Materias primas', unit: 'kg', stock: 38 },
  { code: 'EN0001', name: 'Tineta metálica 16 L', kind: 'Envases', unit: 'UN', stock: 180 },
  { code: 'EQ0001', name: 'Etiqueta tineta 16 L', kind: 'Etiquetas', unit: 'UN', stock: 2400 },
];

export const WAREHOUSE_OPTIONS = ['Bodega JR 1', 'Bodega Planta 2', 'Bodega MP'];

export const RESPONSIBLE_OPTIONS = ['Planta Pinturas', 'Turno A', 'Turno B', 'Jefe de producción'];

const FORMULAS_KEY = 'sqmt.production.formulas.v4';
const BATCHES_KEY = 'sqmt.production.batches.v1';
export const PRODUCTION_CHANGED_EVENT = 'sqmt-production-changed';

const todayIso = () => new Date().toISOString().slice(0, 10);

const isoDaysAgo = (days) => {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date.toISOString().slice(0, 10);
};

const eventAt = (status, at, note = '') => ({
  status,
  at,
  note,
  accounting: BATCH_ACCOUNTING_EVENTS[status] || null,
});

const seedFormulas = () => [
  {
    id: 1,
    formula_kind: FORMULA_KIND_BASE,
    code: 'FO-B-A006-01',
    product_code: 'A006',
    product_name: 'Base epóxica T-COAT',
    color_code: '',
    color_name: '',
    base_formula_id: null,
    base_formula_code: '',
    version: 1,
    yield_qty: 100,
    yield_unit: 'L',
    status: 'Activa',
    notes: 'Vehículo epóxico sin pigmentación de color. Se tintea en fórmulas de color.',
    updated_at: isoDaysAgo(4),
    lines: [
      { item_code: 'RS0001', item_name: 'Resina epóxica', kind: 'Materias primas', quantity: 42, unit: 'kg', scrap_pct: 1.5 },
      { item_code: 'DY0001', item_name: 'Solvente xileno', kind: 'Materias primas', quantity: 12, unit: 'L', scrap_pct: 3 },
      { item_code: 'EX0001', item_name: 'Carbonato de calcio', kind: 'Materias primas', quantity: 28, unit: 'kg', scrap_pct: 0.5 },
      { item_code: 'AD0001', item_name: 'Antiespumante siliconado', kind: 'Materias primas', quantity: 0.8, unit: 'kg', scrap_pct: 0 },
    ],
  },
  {
    id: 2,
    formula_kind: FORMULA_KIND_BASE,
    code: 'FO-B-BW01-02',
    product_code: 'BW01',
    product_name: 'Base primer blanco T-COAT',
    color_code: '',
    color_name: '',
    base_formula_id: null,
    base_formula_code: '',
    version: 2,
    yield_qty: 80,
    yield_unit: 'L',
    status: 'Activa',
    notes: 'Base blanca de primer. Rendimiento tineta 16 L × 5.',
    updated_at: isoDaysAgo(12),
    lines: [
      { item_code: 'RS0001', item_name: 'Resina epóxica', kind: 'Materias primas', quantity: 30, unit: 'kg', scrap_pct: 1 },
      { item_code: 'PG0001', item_name: 'Dióxido de titanio', kind: 'Materias primas', quantity: 26, unit: 'kg', scrap_pct: 1.2 },
      { item_code: 'DY0001', item_name: 'Solvente xileno', kind: 'Materias primas', quantity: 8, unit: 'L', scrap_pct: 2 },
    ],
  },
  {
    id: 3,
    formula_kind: FORMULA_KIND_COLOR,
    code: 'FO-C-A006-03',
    product_code: 'A006',
    product_name: 'Pintura epóxica gris T-COAT',
    color_code: 'RAL 7035',
    color_name: 'Gris claro',
    base_formula_id: 1,
    base_formula_code: 'FO-B-A006-01',
    version: 3,
    yield_qty: 100,
    yield_unit: 'L',
    status: 'Activa',
    notes: 'Color sobre base epóxica. Merma estándar 2%.',
    updated_at: isoDaysAgo(4),
    lines: [
      { item_code: 'FO-B-A006-01', item_name: 'Base epóxica T-COAT', kind: 'Base', quantity: 92, unit: 'L', scrap_pct: 1 },
      { item_code: 'PG0001', item_name: 'Dióxido de titanio', kind: 'Materias primas', quantity: 6.5, unit: 'kg', scrap_pct: 1 },
      { item_code: 'PG0002', item_name: 'Negro de humo furnace', kind: 'Materias primas', quantity: 0.9, unit: 'kg', scrap_pct: 1 },
    ],
  },
  {
    id: 4,
    formula_kind: FORMULA_KIND_COLOR,
    code: 'FO-C-A006-02',
    product_code: 'A006',
    product_name: 'Pintura epóxica gris T-COAT',
    color_code: 'RAL 7035',
    color_name: 'Gris claro',
    base_formula_id: 1,
    base_formula_code: 'FO-B-A006-01',
    version: 2,
    yield_qty: 100,
    yield_unit: 'L',
    status: 'Obsoleta',
    notes: 'Reemplazada por FO-C-A006-03.',
    updated_at: isoDaysAgo(40),
    lines: [
      { item_code: 'FO-B-A006-01', item_name: 'Base epóxica T-COAT', kind: 'Base', quantity: 90, unit: 'L', scrap_pct: 1 },
      { item_code: 'PG0001', item_name: 'Dióxido de titanio', kind: 'Materias primas', quantity: 8, unit: 'kg', scrap_pct: 1 },
      { item_code: 'DY0001', item_name: 'Solvente xileno', kind: 'Materias primas', quantity: 4, unit: 'L', scrap_pct: 2 },
    ],
  },
  {
    id: 5,
    formula_kind: FORMULA_KIND_COLOR,
    code: 'FO-C-A012-01',
    product_code: 'A012',
    product_name: 'Esmalte acrílico interior',
    color_code: 'SW 7008',
    color_name: 'Alabaster',
    base_formula_id: 2,
    base_formula_code: 'FO-B-BW01-02',
    version: 1,
    yield_qty: 100,
    yield_unit: 'L',
    status: 'Borrador',
    notes: 'Pendiente de validación de color en laboratorio.',
    updated_at: isoDaysAgo(1),
    lines: [
      { item_code: 'FO-B-BW01-02', item_name: 'Base primer blanco T-COAT', kind: 'Base', quantity: 96, unit: 'L', scrap_pct: 1 },
      { item_code: 'PG0001', item_name: 'Dióxido de titanio', kind: 'Materias primas', quantity: 3.2, unit: 'kg', scrap_pct: 1 },
    ],
  },
];

const seedBatches = () => [
  {
    id: 1,
    code: 'OP-2026-014',
    formula_id: 3,
    formula_code: 'FO-C-A006-03',
    request_code: 'SP-2026-008',
    color_code: 'RAL 7035',
    formula_kind: 'color',
    product_code: 'A006',
    product_name: 'Pintura epóxica gris T-COAT',
    planned_qty: 320,
    actual_qty: 0,
    unit: 'L',
    status: 'En proceso',
    planned_date: isoDaysAgo(2),
    warehouse: 'Bodega JR 1',
    responsible: 'Turno A',
    notes: 'Pedido comercial COT-1842.',
    created_at: isoDaysAgo(3),
    events: [
      eventAt('Borrador', isoDaysAgo(3), 'Lote creado'),
      eventAt('Planificada', isoDaysAgo(3), 'Programado turno A'),
      eventAt('Emitida', isoDaysAgo(2), 'MP consumida hacia WIP'),
      eventAt('En proceso', isoDaysAgo(2), 'Molino 2 en marcha'),
    ],
  },
  {
    id: 2,
    code: 'OP-2026-015',
    formula_id: 2,
    formula_code: 'FO-B-BW01-02',
    formula_kind: 'base',
    product_code: 'BW01',
    product_name: 'Primer blanco T-COAT',
    planned_qty: 160,
    actual_qty: 158,
    unit: 'L',
    status: 'En calidad',
    planned_date: isoDaysAgo(5),
    warehouse: 'Bodega Planta 2',
    responsible: 'Turno B',
    notes: 'Espera liberación QC viscosidad.',
    created_at: isoDaysAgo(6),
    events: [
      eventAt('Borrador', isoDaysAgo(6)),
      eventAt('Planificada', isoDaysAgo(6)),
      eventAt('Emitida', isoDaysAgo(5), 'MP → WIP'),
      eventAt('En proceso', isoDaysAgo(5)),
      eventAt('Fabricado', isoDaysAgo(4), 'WIP → producto fabricado'),
      eventAt('En calidad', isoDaysAgo(4), 'Muestra enviada a laboratorio'),
    ],
  },
  {
    id: 3,
    code: 'OP-2026-012',
    formula_id: 3,
    formula_code: 'FO-C-A006-03',
    request_code: 'SP-2026-006',
    product_code: 'A006',
    product_name: 'Pintura epóxica gris T-COAT',
    planned_qty: 200,
    actual_qty: 204,
    unit: 'L',
    status: 'Terminado',
    planned_date: isoDaysAgo(18),
    warehouse: 'Bodega JR 1',
    responsible: 'Planta Pinturas',
    notes: '',
    created_at: isoDaysAgo(20),
    events: [
      eventAt('Borrador', isoDaysAgo(20)),
      eventAt('Planificada', isoDaysAgo(19)),
      eventAt('Emitida', isoDaysAgo(18), 'MP → WIP'),
      eventAt('En proceso', isoDaysAgo(18)),
      eventAt('Fabricado', isoDaysAgo(17), 'WIP → fabricado'),
      eventAt('En calidad', isoDaysAgo(17)),
      eventAt('Terminado', isoDaysAgo(16), 'Liberado a PT'),
    ],
  },
  {
    id: 4,
    code: 'OP-2026-016',
    formula_id: 2,
    formula_code: 'FO-B-BW01-02',
    request_code: 'SP-2026-009',
    product_code: 'BW01',
    product_name: 'Primer blanco T-COAT',
    planned_qty: 80,
    actual_qty: 0,
    unit: 'L',
    status: 'Planificada',
    planned_date: todayIso(),
    warehouse: 'Bodega MP',
    responsible: 'Jefe de producción',
    notes: 'Reposición stock bajo.',
    created_at: isoDaysAgo(1),
    events: [eventAt('Borrador', isoDaysAgo(1)), eventAt('Planificada', isoDaysAgo(1), 'Calendario planta')],
  },
  {
    id: 5,
    code: 'OP-2026-011',
    formula_id: 3,
    formula_code: 'FO-C-A006-03',
    color_code: 'RAL 7035',
    formula_kind: 'color',
    product_code: 'A006',
    product_name: 'Pintura epóxica gris T-COAT',
    planned_qty: 96,
    actual_qty: 90,
    unit: 'L',
    status: 'Reproceso',
    planned_date: isoDaysAgo(9),
    warehouse: 'Bodega JR 1',
    responsible: 'Turno A',
    notes: 'QC rechazó brillo. Ajuste de molienda.',
    created_at: isoDaysAgo(11),
    events: [
      eventAt('Borrador', isoDaysAgo(11)),
      eventAt('Planificada', isoDaysAgo(10)),
      eventAt('Emitida', isoDaysAgo(9), 'MP → WIP'),
      eventAt('En proceso', isoDaysAgo(9)),
      eventAt('Fabricado', isoDaysAgo(8), 'WIP → fabricado'),
      eventAt('En calidad', isoDaysAgo(8)),
      eventAt('Reproceso', isoDaysAgo(7), 'No conforme: brillo fuera de rango'),
    ],
  },
];

const readJson = (key, fallback) => {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return fallback();
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) && parsed.length ? parsed : fallback();
  } catch {
    return fallback();
  }
};

const writeJson = (key, value) => {
  window.localStorage.setItem(key, JSON.stringify(value));
  window.dispatchEvent(new Event(PRODUCTION_CHANGED_EVENT));
};

export const loadFormulas = () =>
  readJson(FORMULAS_KEY, seedFormulas).map((row) => ({
    ...row,
    formula_kind: normalizeFormulaKind(row),
    color_code: row.color_code || '',
    color_name: row.color_name || '',
    base_formula_id: row.base_formula_id || null,
    base_formula_code: row.base_formula_code || '',
  }));
export const loadBatches = () => readJson(BATCHES_KEY, seedBatches);

export const saveFormulas = (rows) => writeJson(FORMULAS_KEY, rows);
export const saveBatches = (rows) => writeJson(BATCHES_KEY, rows);

export const formatQty = (value, unit = '', formatCode = '') => {
  const converted = inventoryQtyFromPurchase(value, unit, formatCode);
  const formatted = Number(converted.qty || 0).toLocaleString('es-CL', { maximumFractionDigits: 2 });
  return converted.unit ? `${formatted} ${converted.unit}` : formatted;
};

export const formatDateEs = (value) => {
  if (!value) return '—';
  const date = new Date(`${String(value).slice(0, 10)}T00:00:00`);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString('es-CL');
};

export const productLabel = (code) =>
  PAINT_PRODUCT_OPTIONS.find((option) => option.value === code)?.label || code || '—';

export const materialByCode = (code) => MATERIAL_CATALOG.find((row) => row.code === code) || null;

export const nextFormulaCode = (formulas, productCode, kind = FORMULA_KIND_BASE) => {
  const tag = kind === FORMULA_KIND_COLOR ? 'C' : 'B';
  const prefix = `FO-${tag}-${productCode || 'XXX'}-`;
  const maxVersion = (formulas || [])
    .filter((row) => row.product_code === productCode && normalizeFormulaKind(row) === kind)
    .reduce((max, row) => Math.max(max, Number(row.version) || 0), 0);
  const version = maxVersion + 1;
  return { code: `${prefix}${String(version).padStart(2, '0')}`, version };
};

export const nextBatchCode = (batches) => {
  const year = new Date().getFullYear();
  const prefix = `OP-${year}-`;
  const max = batches
    .map((row) => Number(String(row.code || '').replace(prefix, '')))
    .filter((n) => Number.isFinite(n))
    .reduce((acc, n) => Math.max(acc, n), 0);
  return `${prefix}${String(max + 1).padStart(3, '0')}`;
};

export const scaleFormulaLines = (formula, plannedQty) => {
  const yieldQty = Number(formula?.yield_qty) || 1;
  const factor = Number(plannedQty || 0) / yieldQty;
  return (formula?.lines || []).map((line) => {
    const theoretical = Number(line.quantity || 0) * factor;
    const withScrap = theoretical * (1 + Number(line.scrap_pct || 0) / 100);
    return {
      ...line,
      theoretical_qty: theoretical,
      required_qty: withScrap,
    };
  });
};

export const consumptionGaps = (formula, plannedQty) =>
  scaleFormulaLines(formula, plannedQty).map((line) => {
    const material = materialByCode(line.item_code);
    const stock = Number(material?.stock || 0);
    return {
      ...line,
      stock,
      short: stock < Number(line.required_qty || 0),
    };
  });

export const nextBatchStatuses = (status) => {
  switch (status) {
    case 'Borrador':
      return ['Planificada', 'Anulada'];
    case 'Planificada':
      return ['Emitida', 'Anulada'];
    case 'Emitida':
      return ['En proceso'];
    case 'En proceso':
      return ['Fabricado'];
    case 'Fabricado':
      return ['En calidad'];
    case 'En calidad':
      return ['Terminado', 'Reproceso'];
    case 'Reproceso':
      return ['En proceso'];
    default:
      return [];
  }
};

export const transitionLabel = (status) => {
  switch (status) {
    case 'Planificada':
      return 'Planificar';
    case 'Emitida':
      return 'Emitir (MP → WIP)';
    case 'En proceso':
      return 'Iniciar proceso';
    case 'Fabricado':
      return 'Marcar fabricado';
    case 'En calidad':
      return 'Enviar a calidad';
    case 'Terminado':
      return 'Liberar a PT';
    case 'Reproceso':
      return 'Devolver a reproceso';
    case 'Anulada':
      return 'Anular';
    default:
      return status;
  }
};

export const emptyFormula = (kind = FORMULA_KIND_BASE) => ({
  id: null,
  formula_kind: kind === FORMULA_KIND_COLOR ? FORMULA_KIND_COLOR : FORMULA_KIND_BASE,
  code: '',
  product_code: '',
  product_name: '',
  color_code: '',
  color_name: '',
  base_formula_id: '',
  base_formula_code: '',
  version: 1,
  yield_qty: 100,
  yield_unit: 'L',
  status: 'Borrador',
  notes: '',
  lines: [{ item_code: '', item_name: '', kind: 'Materias primas', quantity: 0, unit: 'kg', scrap_pct: 0 }],
});

export const emptyBatch = () => ({
  id: null,
  code: '',
  formula_id: '',
  formula_code: '',
  request_code: '',
  product_code: '',
  product_name: '',
  color_code: '',
  formula_kind: FORMULA_KIND_BASE,
  planned_qty: 100,
  actual_qty: 0,
  unit: 'L',
  format: 'Tineta 16 L',
  status: 'Borrador',
  process_stage: 'planning',
  planned_date: todayIso(),
  warehouse: WAREHOUSE_OPTIONS[0],
  responsible: RESPONSIBLE_OPTIONS[0],
  notes: '',
  events: [],
});

export const inventoryStagesFromBatches = (batches = []) => {
  const qty = (statuses) =>
    batches
      .filter((row) => statuses.includes(row.status))
      .reduce((acc, row) => acc + Number(row.actual_qty || row.planned_qty || 0), 0);

  return [
    {
      key: 'mp',
      label: 'Materia prima',
      account: '1.2.02',
      hint: 'Stock de adquisiciones disponible para emitir lotes.',
      batches: 0,
      qty: MATERIAL_CATALOG.filter((row) => row.kind === 'Materias primas').reduce((acc, row) => acc + row.stock, 0),
      unit: 'u. físicas',
    },
    {
      key: 'wip',
      label: 'Producto en proceso',
      account: '1.2.05',
      hint: 'Lotes emitidos o en molienda / mezclado.',
      batches: batches.filter((row) => ['Emitida', 'En proceso', 'Reproceso'].includes(row.status)).length,
      qty: qty(['Emitida', 'En proceso', 'Reproceso']),
      unit: 'L',
    },
    {
      key: 'manufactured',
      label: 'Producto fabricado',
      account: '1.2.06',
      hint: 'Fabricado, pendiente o en control de calidad.',
      batches: batches.filter((row) => ['Fabricado', 'En calidad'].includes(row.status)).length,
      qty: qty(['Fabricado', 'En calidad']),
      unit: 'L',
    },
    {
      key: 'pt',
      label: 'Producto terminado',
      account: '1.2.07',
      hint: 'Liberado a despacho (producción.finished).',
      batches: batches.filter((row) => row.status === 'Terminado').length,
      qty: qty(['Terminado']),
      unit: 'L',
    },
  ];
};
